import { useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//components
import {
  Section,
  Kicker,
  KickerDot,
  SectionTitle,
} from '@/containers-ai/SectionHeading';

//styles
import {
  Theater,
  Tabs,
  Tab,
  TabNumber,
  TabTitle,
  TabSubtitle,
  StageBox,
  StageHead,
  StageHeadTitle,
  StageHeadTag,
  StageStatus,
  StageLog,
  LogRow,
  LogTime,
  LogText,
  StageSummary,
  StageSummaryLabel,
  StageFoot,
} from './ScenarioTheater.styles';
import { Container } from '@/components/Grid';

const LINE_INTERVAL = 950;
const AUTO_ADVANCE_INTERVAL = 9500;

// Order these slugs render in — matches the JSON keys in content.scenarios.
const SCENARIO_SLUGS = [
  'document_collection_and_filing',
  'tracking_and_status_updates',
  'tender_failures',
  'proactive_load_audits',
  'claims_processing',
  'scheduling_and_re_scheduling',
];

export function ScenarioTheater({ content }) {
  const SCENARIOS = SCENARIO_SLUGS.map((slug) => {
    const scenario = content.scenarios[slug];
    return {
      ...scenario,
      lines: scenario.lines.map((line) => ({
        ...line,
        text: parse(line.text),
      })),
      summary: parse(scenario.summary),
    };
  });

  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const [done, setDone] = useState(false);
  const lineTimerRef = useRef(null);
  const autoAdvanceRef = useRef(null);
  const userInteractedRef = useRef(false);

  const active = SCENARIOS[activeIndex];

  function selectScenario(index) {
    userInteractedRef.current = true;
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    setActiveIndex(index);
  }

  useEffect(() => {
    if (prefersReducedMotion || userInteractedRef.current) return undefined;

    autoAdvanceRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % SCENARIOS.length);
    }, AUTO_ADVANCE_INTERVAL);

    return () => clearInterval(autoAdvanceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (lineTimerRef.current) clearInterval(lineTimerRef.current);
    setDone(false);

    if (prefersReducedMotion) {
      setVisibleLines(active.lines.length);
      setDone(true);
      return undefined;
    }

    setVisibleLines(0);
    let li = 0;
    lineTimerRef.current = setInterval(() => {
      if (li < active.lines.length) {
        li += 1;
        setVisibleLines(li);
      } else {
        clearInterval(lineTimerRef.current);
        lineTimerRef.current = null;
        setDone(true);
      }
    }, LINE_INTERVAL);

    return () => clearInterval(lineTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, prefersReducedMotion]);

  return (
    <Section id="scenario-theater">
      <Container>
        {content.kicker && (
          <Kicker>
            <KickerDot />
            {content.kicker}
          </Kicker>
        )}
        <SectionTitle>{content.title}</SectionTitle>

        <Theater>
          <Tabs role="tablist">
            {SCENARIOS.map((scenario, index) => (
              <Tab
                key={scenario.tab}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                $active={index === activeIndex}
                onClick={() => selectScenario(index)}
              >
                <TabNumber $active={index === activeIndex}>
                  {String(index + 1).padStart(2, '0')}
                </TabNumber>
                <div>
                  <TabTitle $active={index === activeIndex}>
                    {scenario.tab}
                  </TabTitle>
                  <TabSubtitle>{scenario.td}</TabSubtitle>
                </div>
              </Tab>
            ))}
          </Tabs>

          <StageBox>
            <StageHead>
              <div>
                <StageHeadTitle>{active.nite}</StageHeadTitle>
                <StageHeadTag>{active.ref}</StageHeadTag>
              </div>
              <StageStatus $done={done}>
                {done ? content.statusResolved : content.statusWorking}
              </StageStatus>
            </StageHead>
            <StageLog>
              {active.lines.map((line, index) => (
                <LogRow
                  key={index}
                  $highlight={line.highlight}
                  $show={index < visibleLines}
                >
                  <LogTime>{line.time}</LogTime>
                  <LogText $highlight={line.highlight}>{line.text}</LogText>
                </LogRow>
              ))}
            </StageLog>
            <StageSummary $show={done}>
              <StageSummaryLabel>{content.summaryLabel}</StageSummaryLabel>
              {active.summary}
            </StageSummary>
            <StageFoot>
              <span>{content.sessionLabel}</span>
              <span>{active.agent}</span>
            </StageFoot>
          </StageBox>
        </Theater>
      </Container>
    </Section>
  );
}
